import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Users,
  User,
  Camera,
  Settings2,
  ChevronDown,
  ChevronUp,
  Loader2,
} from 'lucide-react';

import { usePersons, usePersonPhotos } from '@/api/hooks/useFaces';
import type { Person } from '@/api/types/face.types';

// ---------------------------------------------------------------------------
// Person card with expandable photo gallery
// ---------------------------------------------------------------------------

function PersonCard({ person }: { person: Person }) {
  const { t } = useTranslation('faces');
  const [expanded, setExpanded] = useState(false);
  const { data: photosData, isLoading } = usePersonPhotos(
    expanded ? person.id : '',
    { page_size: 20 },
  );

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-900">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-4 py-3"
      >
        {person.representative_face_url ? (
          <img
            src={person.representative_face_url}
            alt={person.name || t('unknownPerson')}
            className="h-12 w-12 rounded-full object-cover ring-2 ring-white dark:ring-gray-800"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
            <User className="h-6 w-6 text-gray-400" />
          </div>
        )}

        <div className="min-w-0 flex-1 text-start">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {person.name || t('unknownPerson')}
          </p>
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            {person.role && person.role !== 'unknown' && (
              <span className="rounded-full bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                {t(`roles.${person.role}`)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Camera className="h-3 w-3" />
              {person.photo_count}
            </span>
          </div>
        </div>

        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded photo grid */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
            </div>
          ) : photosData && photosData.results.length > 0 ? (
            <>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
                {photosData.results.map((photo) => (
                  <div
                    key={photo.id}
                    className="aspect-square overflow-hidden rounded-lg"
                  >
                    <img
                      src={photo.thumbnail_small}
                      alt={photo.original_filename}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
              {photosData.count > 20 && (
                <p className="mt-2 text-center text-xs text-gray-400">
                  +{photosData.count - 20} {t('common:gallery.photos')}
                </p>
              )}
            </>
          ) : (
            <p className="py-2 text-center text-xs text-gray-400">
              {t('common:status.noResults')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main faces tab
// ---------------------------------------------------------------------------

export function EventFacesTab({ eventId }: { eventId: string }) {
  const { t } = useTranslation('faces');
  const { data, isLoading } = usePersons({ event: eventId, page_size: 100 });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  const persons = data?.results ?? [];
  const namedCount = persons.filter((p) => p.name).length;

  if (persons.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-16 dark:border-gray-600">
        <Users className="h-12 w-12 text-gray-400 dark:text-gray-500" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('noPeopleYet')}
        </p>
        <Link
          to={`/faces?event=${eventId}`}
          className="btn-primary mt-4"
        >
          {t('runFaceWizard')}
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {persons.length} {t('personCount')}
          </span>
          {namedCount > 0 && (
            <span>
              {namedCount} {t('namedPeople')}
            </span>
          )}
        </div>
        <Link
          to={`/faces?event=${eventId}`}
          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
        >
          <Settings2 className="h-3.5 w-3.5" />
          {t('runFaceWizard')}
        </Link>
      </div>

      {/* People grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {persons.map((person) => (
          <PersonCard key={person.id} person={person} />
        ))}
      </div>
    </div>
  );
}
